import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Form, Modal, TreeSelect, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  buildFolderDestinationOptions,
  performDocumentMove,
  ROOT_DESTINATION,
} from "./documentMoveUtils";

const { Text } = Typography;

// "Move to folder" — the keyboard/menu counterpart to dragging a document
// onto a folder in the sidebar tree. Destination options come from the
// already-loaded matterDetailsTree, so no extra fetch is needed to open it.
const MoveDocumentModal = ({
  open,
  onClose,
  documentId,
  documentName = "",
  currentFolderId = null,
  matterId = null,
  documentRecord = null,
}) => {
  const dispatch = useDispatch();
  const matterDetailsTree = useSelector(
    (state) => state.matterDetails?.matterDetailsTree
  );

  const [destination, setDestination] = useState(null);
  const [moving, setMoving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDestination(null);
    setMoving(false);
    setSubmitError("");
  }, [open]);

  const options = useMemo(
    () => buildFolderDestinationOptions(matterDetailsTree, currentFolderId),
    [matterDetailsTree, currentFolderId]
  );

  const destinationLabel = useMemo(() => {
    if (!destination) return "";
    if (destination === ROOT_DESTINATION) return "Matter files (top level)";
    const findTitle = (nodes) => {
      for (const node of nodes || []) {
        if (node.value === destination) return node.title;
        const nested = findTitle(node.children);
        if (nested) return nested;
      }
      return "";
    };
    return findTitle(options);
  }, [destination, options]);

  const handleMove = async () => {
    if (!documentId || !destination || moving) return;
    setMoving(true);
    setSubmitError("");
    const result = await dispatch(
      performDocumentMove({
        documentId,
        documentName,
        destinationFolderId: destination,
        destinationName:
          destination === ROOT_DESTINATION ? "" : destinationLabel,
        matterId,
        documentRecord: documentRecord || undefined,
      })
    );
    if (result?.ok) {
      onClose();
    } else {
      setSubmitError(result?.message || "Couldn't move the document — try again.");
      setMoving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Move to folder"
      okText={moving ? "Moving…" : "Move"}
      okButtonProps={{
        disabled: !destination || moving,
        "data-testid": "move-document-submit",
      }}
      cancelButtonProps={{ disabled: moving }}
      onOk={handleMove}
      onCancel={moving ? undefined : onClose}
      maskClosable={!moving}
      destroyOnHidden
      data-testid="move-document-modal"
    >
      <Form layout="vertical">
        <Form.Item label="Document" style={{ marginBottom: 8 }}>
          <Text strong>{documentName || "—"}</Text>
        </Form.Item>
        <Form.Item label="Move to" required>
          <TreeSelect
            treeData={options}
            value={destination || undefined}
            onChange={(value) => setDestination(value)}
            placeholder="Choose a destination folder"
            showSearch
            treeNodeFilterProp="title"
            treeDefaultExpandAll
            disabled={moving}
            style={{ width: "100%" }}
            data-testid="move-document-destination"
          />
        </Form.Item>
        {submitError && (
          <Text type="danger" data-testid="move-document-error">
            {submitError}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

MoveDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documentName: PropTypes.string,
  currentFolderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documentRecord: PropTypes.object,
};

export default MoveDocumentModal;
